import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getLivisUserInfo } from "../../../helpers/storage";
import {
  AccountsIcon,
  ArchiveIcon,
  AuditLogIcon,
  ClassLibraryIcon,
  HelpCenterIcon,
  LivisWalletIcon,
  SidebarSettingCard,
  SyntheticDataIcon,
  TagManagementIcon,
  AlertBoldIcon,
  LivisStorageIcon,
  SuperAdminDashboardIcon,
} from "../../Icons/LivisIcons";
import { doesUserHasAccess } from "../../../helpers/utils";

const ACCOUNTS_ROUTES = [
  "/app/manage/apps",
  "/app/manage/permissions",
  "/app/manage/roles",
  "/app/manage/users",
  "/app/user/MyProfile",
  "/app/client/user/manage",
];

// Key used to identify the Accounts accordion/menu inside itemRefs
const ACCOUNTS_KEY = "panel3";

const SideBarMenu = (props: any = null) => {
  const { t } = useTranslation();
  const location = useLocation().pathname;
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>("");

  const [hamburgerEl, setHamburgerEl] = useState<null | HTMLElement>(null);
  const openHamburger = Boolean(hamburgerEl);

  // NEW: holds a DOM ref for every clickable item (tabs + accounts entry points)
  // so we can scroll whichever one is active into view.
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (location.includes("/app/user")) {
      setExpanded("panel3");
    }
  }, [location]);

  const [showUserPanel, setShowuserPanel] = useState<any>(false);
  const [showAuditPanel, setShowAuditPanel] = useState<any>(false);
  const [showTagPanel, setShowTagPanel] = useState<any>(false);
  const [isSuperuser, setIsSuperuser] = useState<any>(false);

  useEffect(() => {
    const user_info_string: any = getLivisUserInfo();
    const user_info = JSON.parse(user_info_string);
    const permissions = user_info?.user_permissions;
    const superuserStatus = user_info?.is_superuser;
    setIsSuperuser(superuserStatus);

    if (superuserStatus) {
      setShowAuditPanel(true);
      setShowuserPanel(true);
      setShowTagPanel(true);
    }
    if (permissions?.includes(151)) {
      setShowuserPanel(true);
    }
    if (permissions?.includes(93)) {
      setShowAuditPanel(true);
    }
    if ((permissions?.includes(94) || permissions?.includes(139)) && permissions?.includes(142)) {
      setShowTagPanel(true);
    }
  }, []);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleNavigation = (route: string) => {
    navigate(route);
    setHamburgerEl(null);
  };

  const handleHamburgerMenu = (event: React.MouseEvent<HTMLElement>) => {
    setHamburgerEl((current) => (current ? null : event.currentTarget));
  };

  const isAccountsActive =
    ACCOUNTS_ROUTES.includes(location) || expanded === "panel3";

  const tabs = [
    {
      key: "tag-management",
      show: showTagPanel,
      active: location === "/app/tag-management",
      icon: <TagManagementIcon className="livis-settings-sb-item-icon" />,
      label: t("Tag Management"),
      onClick: () => handleNavigation("/app/tag-management"),
    },
    {
      key: "audit-log",
      show: showAuditPanel,
      active: location === "/app/logs",
      icon: <AuditLogIcon className="livis-settings-sb-item-icon" />,
      label: t("Audit Log"),
      onClick: () => handleNavigation("/app/logs"),
    },
    {
      key: "subscription",
      show: doesUserHasAccess(68) || doesUserHasAccess(79),
      active: location === "/app/subscription",
      icon: <LivisWalletIcon className="livis-settings-sb-item-icon" />,
      label: t("Subscription"),
      onClick: () => handleNavigation("/app/subscription"),
    },
    {
      key: "help-center",
      show: doesUserHasAccess(156),
      active: location === "/app/help-center",
      icon: <HelpCenterIcon className="livis-settings-sb-item-icon" />,
      label: t("Help Center"),
      onClick: () => handleNavigation("/app/help-center"),
    },
    {
      key: "synthetic-data",
      show: isSuperuser,
      active: location === "/app/synthetic-data/list",
      icon: <SyntheticDataIcon className="livis-settings-sb-item-icon" />,
      label: t("Synthetic Data"),
      onClick: () => handleNavigation("/app/synthetic-data/list"),
    },
    {
      key: "defect-library",
      show: isSuperuser,
      active: location === "/app/defect-library",
      icon: <ClassLibraryIcon className="livis-settings-sb-item-icon" />,
      label: t("Defect Dataset Library"),
      onClick: () => handleNavigation("/app/defect-library"),
    },
    {
      key: "admin-dashboard",
      show: isSuperuser,
      active: location === "/app/supervisor/dashboard",
      icon: <SuperAdminDashboardIcon className="livis-settings-sb-item-icon" />,
      label: t("Admin Dashboard"),
      onClick: () => handleNavigation("/app/supervisor/dashboard"),
    },
    {
      key: "ongoing-training",
      show: isSuperuser,
      active: location === "/app/ongoing-training",
      icon: <SuperAdminDashboardIcon className="livis-settings-sb-item-icon" />,
      label: t("Ongoing Training"),
      onClick: () => handleNavigation("/app/ongoing-training"),
    },
    {
      key: "archive-settings",
      show: doesUserHasAccess(170) || doesUserHasAccess(169),
      active: location === "/app/archive-settings",
      icon: <ArchiveIcon className="livis-settings-sb-item-icon" />,
      label: t("Archive Settings"),
      onClick: () => handleNavigation("/app/archive-settings"),
    },
    {
      key: "notifications",
      show: doesUserHasAccess(173) || doesUserHasAccess(174),
      active: location === "/app/notifications",
      icon: <AlertBoldIcon className="livis-settings-sb-item-icon" />,
      label: t("Notification Settings"),
      onClick: () => handleNavigation("/app/notifications"),
    },
    {
      key: "livis-storage",
      show: isSuperuser,
      active: location === "/app/storage-configuration",
      icon: <LivisStorageIcon className="livis-settings-sb-item-icon" />,
      label: t("LIVIS Storage"),
      onClick: () => handleNavigation("/app/storage-configuration"),
    },
    {
      key: "addon-requests",
      show: isSuperuser,
      active: location === "/app/addon-requests",
      icon: <LivisWalletIcon className="livis-settings-sb-item-icon" />,
      label: t("Addon Requests"),
      onClick: () => handleNavigation("/app/addon-requests"),
    },
    {
      key: "ai-knowledge-base",
      show: isSuperuser,
      active: location === "/app/ai-assistant-knowledge-base",
      icon: <ArchiveIcon className="livis-settings-sb-item-icon" />,
      label: t("AI Knowledge Base"),
      onClick: () => handleNavigation("/app/ai-assistant-knowledge-base"),
    },
    {
      key: "license-management",
      show: doesUserHasAccess(191) || doesUserHasAccess(188),
      active: location.startsWith("/app/license-management"),
      icon: <LivisStorageIcon className="livis-settings-sb-item-icon" />,
      label: t("License Management"),
      onClick: () => handleNavigation("/app/license-management"),
    },
    {
      key: "terms-and-conditions",
      show: isSuperuser,
      active: location.includes("/app/terms-and-conditions"),
      icon: <ClassLibraryIcon className="livis-settings-sb-item-icon" />,
      label: t("Terms & Conditions"),
      onClick: () => handleNavigation("/app/terms-and-conditions"),
    },
    {
      key: "plan-license-usage",
      show: isSuperuser,
      active: location.startsWith("/app/plan-license-usage"),
      icon: <LivisWalletIcon className="livis-settings-sb-item-icon" />,
      label: t("Plan & License Usage"),
      onClick: () => handleNavigation("/app/plan-license-usage"),
    },
    {
      key: "plan-management",
      show: isSuperuser,
      active: location.startsWith("/app/plan-management"),
      icon: <LivisWalletIcon className="livis-settings-sb-item-icon" />,
      label: t("Plan & Addon Management"),
      onClick: () => handleNavigation("/app/plan-management"),
    },
  ];

  const showManageUsers = showUserPanel && doesUserHasAccess(151);
  const showMyProfile = !isSuperuser;

  // NEW: whenever the route changes, scroll whichever item is now active
  // into view. `block`/`inline` both set to "nearest" so this works
  // regardless of whether the surrounding container scrolls vertically
  // (desktop rail) or horizontally (the <=1512px tab strip) — it only
  // moves the element if it's actually clipped, in whichever direction
  // the nearest scrollable ancestor scrolls.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const activeKey = isAccountsActive
        ? ACCOUNTS_KEY
        : tabs.find((tab) => tab.active)?.key;

      if (activeKey && itemRefs.current[activeKey]) {
        itemRefs.current[activeKey]?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [location, isSuperuser, showUserPanel, showAuditPanel, showTagPanel]);

  return (
    <Grid className="livis-settings-sb-root">
      <div className="livis-settings-sb-list-wrap">
        <div className="livis-settings-sb-header-card">
          <SidebarSettingCard className="livis-settings-sb-header-badge" />
          <div className="livis-settings-sb-header-title">{t("Settings")}</div>
        </div>

        <div className="livis-settings-sb-tabs-card">
          <div className="livis-settings-sb-accordion-desktop">
            <Accordion
              expanded={isAccountsActive}
              onChange={handleChange("panel3")}
              className="livis-settings-sb-accordion"
              ref={(el: HTMLDivElement | null) => (itemRefs.current[ACCOUNTS_KEY] = el)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={isAccountsActive ? "livis-settings-sb-accordion-summary-active" : ""}
              >
                <AccountsIcon className="livis-settings-sb-item-icon" />
                <Typography className="livis-settings-sb-accordion-heading">
                  {t("Accounts")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="livis-settings-sb-accordion-details">
                {showManageUsers && (
                  <div
                    className={
                      location === "/app/manage/users" || location === "/app/client/user/manage"
                        ? "livis-settings-sb-sub-item-active"
                        : "livis-settings-sb-sub-item"
                    }
                    onClick={() => handleNavigation("/app/client/user/manage")}
                  >
                    {t("Manage Users")}
                  </div>
                )}
                {showMyProfile && (
                  <div
                    className={
                      location === "/app/user/MyProfile"
                        ? "livis-settings-sb-sub-item-active"
                        : "livis-settings-sb-sub-item"
                    }
                    onClick={() => handleNavigation("/app/user/MyProfile")}
                  >
                    {t("My Profile")}
                  </div>
                )}
              </AccordionDetails>
            </Accordion>
          </div>

          <div className="livis-settings-sb-flex-wrap">
            <div className="livis-settings-sb-accordion-mobile">
              <div
                className={isAccountsActive ? "livis-settings-sb-item-active" : "livis-settings-sb-item"}
                onClick={handleHamburgerMenu}
                ref={(el: HTMLDivElement | null) => {
                  // Only register this as the "panel3" ref on layouts where
                  // the desktop accordion isn't rendered/visible; harmless
                  // either way since only one will actually be in the DOM
                  // for a given breakpoint via CSS display rules.
                  if (!itemRefs.current[ACCOUNTS_KEY]) {
                    itemRefs.current[ACCOUNTS_KEY] = el;
                  }
                }}
              >
                <AccountsIcon className="livis-settings-sb-item-icon" />
                <div className="livis-settings-sb-mobile-accordion-title">
                  {t("Accounts")} <ExpandMoreIcon />
                </div>
                <Menu
                  anchorEl={hamburgerEl}
                  open={openHamburger}
                  onClose={() => setHamburgerEl(null)}
                  onClick={() => setHamburgerEl(null)}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  {showManageUsers && (
                    <MenuItem onClick={() => handleNavigation("/app/client/user/manage")}>
                      {t("Manage Users")}
                    </MenuItem>
                  )}
                  {showManageUsers && showMyProfile && <Divider />}
                  {showMyProfile && (
                    <MenuItem onClick={() => handleNavigation("/app/user/MyProfile")}>
                      {t("My Profile")}
                    </MenuItem>
                  )}
                </Menu>
              </div>
            </div>

            <div className="livis-settings-sb-items-list">
              {tabs
                .filter((tab) => tab.show)
                .map((tab) => (
                  <div
                    key={tab.key}
                    ref={(el) => (itemRefs.current[tab.key] = el)}
                    className={tab.active ? "livis-settings-sb-item-active" : "livis-settings-sb-item"}
                    onClick={tab.onClick}
                  >
                    {tab.icon}
                    <span className="livis-settings-sb-item-label">{tab.label}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default SideBarMenu;